import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FileText, Clock, CheckCircle2, FileEdit, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "@/components/admin/AdminLayout";

export const Route = createFileRoute("/admin/dashboard")({
  component: DashboardPage,
  head: () => ({ meta: [{ title: "Dashboard — Admin" }, { name: "robots", content: "noindex" }] }),
});

function DashboardPage() {
  const { data: stats } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: async () => {
      const [all, published, scheduled, drafts] = await Promise.all([
        supabase.from("posts").select("id", { count: "exact", head: true }),
        supabase.from("posts").select("id", { count: "exact", head: true }).eq("status", "published"),
        supabase.from("posts").select("id", { count: "exact", head: true }).eq("status", "scheduled"),
        supabase.from("posts").select("id", { count: "exact", head: true }).eq("status", "draft"),
      ]);
      return {
        total: all.count ?? 0,
        published: published.count ?? 0,
        scheduled: scheduled.count ?? 0,
        drafts: drafts.count ?? 0,
      };
    },
  });

  const { data: recent } = useQuery({
    queryKey: ["admin", "recent-posts"],
    queryFn: async () => {
      const { data } = await supabase
        .from("posts")
        .select("id, title, status, updated_at")
        .order("updated_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  const cards = [
    { label: "All Posts", value: stats?.total ?? "—", icon: FileText, color: "bg-sky-50 text-sky-600" },
    { label: "Published", value: stats?.published ?? "—", icon: CheckCircle2, color: "bg-green-50 text-green-600" },
    { label: "Scheduled", value: stats?.scheduled ?? "—", icon: Clock, color: "bg-amber-50 text-amber-600" },
    { label: "Drafts", value: stats?.drafts ?? "—", icon: FileEdit, color: "bg-slate-100 text-slate-600" },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{c.label}</div>
                <div className="mt-2 text-3xl font-bold text-slate-900">{c.value}</div>
              </div>
              <div className={`p-3 rounded-lg ${c.color}`}><c.icon className="h-5 w-5" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center mb-8">
        <Link to="/admin/posts/new">
          <Button size="lg" className="bg-sky-600 hover:bg-sky-700 px-8 py-6 text-base">
            <Plus className="h-5 w-5 mr-2" /> Add New Blog
          </Button>
        </Link>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800">Recent Posts</h2>
          <Link to="/admin/posts" className="text-sm text-sky-600 hover:underline">View all</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {(recent ?? []).length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No posts yet.{" "}
              <Link to="/admin/posts/new" className="text-sky-600 hover:underline">Write your first post</Link>
            </div>
          ) : (
            recent?.map((p) => (
              <Link
                key={p.id}
                to="/admin/posts/$id"
                params={{ id: p.id }}
                className="flex items-center justify-between px-5 py-3 hover:bg-slate-50"
              >
                <div className="font-medium text-slate-800 truncate">{p.title || "(no title)"}</div>
                <StatusPill status={p.status} />
              </Link>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    published: "bg-green-100 text-green-700",
    scheduled: "bg-amber-100 text-amber-700",
    draft: "bg-slate-100 text-slate-600",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[status] ?? ""}`}>{status}</span>;
}
